import { AhiBanner, defineBanner } from "./component.js";
import { BANNER_ENABLED } from "./config.js";
import { resolveBannerTreatment, type BannerTreatment } from "./divisions.js";
import { readOverride } from "./override.js";

const BOOTED_FLAG = "__AHI_BANNER_BOOTED__";

export interface BootAttrs {
  division: string | null;
  /** True if data-is-divisional-page is present with any value other than "false". */
  isDivisionalPage: boolean;
}

/** Read the `data-*` options off the embedding <script> element. */
export function readAttrs(script: Element | null): BootAttrs {
  return {
    division: script?.getAttribute("data-division") ?? null,
    isDivisionalPage:
      (script?.hasAttribute("data-is-divisional-page") ?? false) &&
      script?.getAttribute("data-is-divisional-page") !== "false",
  };
}

/**
 * Mount the banner: fill an existing <ahi-banner> if the page has one (lets a
 * site control exact placement), else insert a fresh element at the top of
 * <body>. Guards against double-injection by reusing any element already
 * present.
 */
export function mount(treatment: BannerTreatment): AhiBanner {
  let el = document.querySelector<AhiBanner>("ahi-banner");
  if (!el) {
    el = document.createElement("ahi-banner") as AhiBanner;
    document.body.insertBefore(el, document.body.firstChild);
  }
  el.treatment = treatment;
  return el;
}

/**
 * Entry orchestration: register the element, read `data-division` (required)
 * and `data-is-divisional-page` (optional), and mount. Never throws into the
 * host page - an unknown or missing division logs a `console.warn` and
 * renders nothing, same fail-safe posture as @ahi/header. A `false`
 * `BANNER_ENABLED` in config.ts also renders nothing, but silently (no
 * console output, no DOM work at all) - that's an intentional global kill
 * switch, not a misconfiguration, so it doesn't warn. A `?ahi-banner=on|off`
 * query param overrides `BANNER_ENABLED` for manual preview - `on` bypasses
 * the kill switch (division resolution below still applies), `off` force-hides
 * regardless of it, both silently, same as the kill switch itself.
 *
 * A one-time flag on `window` guards against this running more than once on
 * the same page (checked before any other work, unlike @ahi/header's `mount()`
 * dedup which only avoids a duplicate *element* - this also skips re-parsing
 * attributes and re-registering the custom element on a second execution).
 */
export function boot(
  script: Element | null = typeof document !== "undefined" ? document.currentScript : null,
  search: string = typeof window !== "undefined" ? window.location.search : "",
): AhiBanner | undefined {
  const w = window as unknown as Record<string, unknown>;
  if (w[BOOTED_FLAG]) return undefined;
  w[BOOTED_FLAG] = true;

  const override = readOverride(search);
  if (override === "off") return undefined;
  if (override !== "on" && !BANNER_ENABLED) return undefined;

  defineBanner();

  const { division, isDivisionalPage } = readAttrs(script);
  const treatment = resolveBannerTreatment(division, isDivisionalPage);
  if (!treatment) {
    console.warn(`[ahi-banner] Unknown or missing data-division "${division}". Banner will not render.`);
    return undefined;
  }

  return mount(treatment);
}
